import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isAdminApiEnabled } from "../../config/adminApiFeature";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearServiceRequestDetail,
  fetchServiceRequestById,
  removeServiceRequest,
  rerunServiceRequestClientEmailAnalysis,
  sendServiceRequestEmail,
  sendServiceRequestReply,
  updateServiceRequest,
} from "../../store/slices/serviceRequestsSlice";
import { AdminTypedDeleteModal } from "./AdminConfirmModals";
import AdminCollapsiblePanel from "./AdminCollapsiblePanel";
import { AdminEmailThreadPanel } from "./AdminEmailThreadPanel";
import RequestAnalysisPanel, { analysisChromeMeta } from "./RequestAnalysisPanel";
import AdminStatusWorkflow from "./AdminStatusWorkflow";
import { adminStatusBadgeClass, adminStatusLabel, canonicalAdminStatus } from "./adminStatusUi";

function empty(s) {
  const t = (s || "").trim();
  return t || "—";
}

export default function AdminRequestEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiOn = isAdminApiEnabled();
  const dispatch = useAppDispatch();
  const detail = useAppSelector((s) => s.serviceRequests.detail);
  const detailStatus = useAppSelector((s) => s.serviceRequests.detailStatus);
  const detailError = useAppSelector((s) => s.serviceRequests.detailError);
  const seenMarkedRef = useRef(null);

  const row = apiOn ? detail : null;

  const [form, setForm] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const clientEmailOutbound = useAppSelector((s) => s.serviceRequests.clientEmailOutbound);
  const clientEmailSendHttpPending = useAppSelector(
    (s) => s.serviceRequests.clientEmailSendHttpPending
  );
  const [editingFields, setEditingFields] = useState(false);
  const [deleteRequestOpen, setDeleteRequestOpen] = useState(false);

  useEffect(() => {
    if (!apiOn) return undefined;
    return () => {
      dispatch(clearServiceRequestDetail());
    };
  }, [apiOn, dispatch]);

  useEffect(() => {
    if (!apiOn || !id) return;
    seenMarkedRef.current = null;
    dispatch(clearServiceRequestDetail());
    dispatch(fetchServiceRequestById(id));
  }, [apiOn, id, dispatch]);

  useEffect(() => {
    if (!apiOn || !id || !row || row.id !== id) return;
    if (seenMarkedRef.current === id) return;
    seenMarkedRef.current = id;
    if (!row.seenAt) {
      dispatch(
        updateServiceRequest({
          id,
          body: { seenAt: new Date().toISOString() },
        })
      );
    }
  }, [apiOn, dispatch, id, row]);

  useEffect(() => {
    if (!row) {
      setForm(null);
      return;
    }
    if (editingFields) return;
    setForm({
      requesterType: row.requesterType || "company",
      name: row.name || "",
      companyName: row.companyName || "",
      email: row.email || "",
      requirements: row.requirements || "",
      description: row.description || "",
      serviceTitle: row.serviceTitle || "",
      status: canonicalAdminStatus(row.status),
    });
  }, [row, editingFields]);

  const handleField = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => (prev ? { ...prev, [name]: value } : prev));
  }, []);

  const setPipelineStatus = useCallback((status) => {
    setForm((prev) => (prev ? { ...prev, status } : prev));
  }, []);

  const handleApplyStatus = useCallback(() => {
    if (!id || !form) return;
    dispatch(updateServiceRequest({ id, body: { status: form.status } }));
  }, [dispatch, form, id]);

  const handleSaveFields = useCallback(
    (e) => {
      e.preventDefault();
      if (!id || !form) return;
      dispatch(
        updateServiceRequest({
          id,
          body: {
            requesterType: form.requesterType,
            name: form.name,
            companyName: form.companyName,
            email: form.email,
            requirements: form.requirements,
            description: form.description,
            serviceTitle: form.serviceTitle,
          },
        })
      );
      setEditingFields(false);
    },
    [dispatch, form, id]
  );

  const handleCancelEdit = useCallback(() => {
    if (row) {
      setForm({
        requesterType: row.requesterType || "company",
        name: row.name || "",
        companyName: row.companyName || "",
        email: row.email || "",
        requirements: row.requirements || "",
        description: row.description || "",
        serviceTitle: row.serviceTitle || "",
        status: canonicalAdminStatus(row.status),
      });
    }
    setEditingFields(false);
  }, [row]);

  const handleReply = useCallback(
    (e) => {
      e.preventDefault();
      if (!id || !replyBody.trim()) return;
      dispatch(
        sendServiceRequestReply({
          id,
          body: replyBody.trim(),
          internal: true,
        })
      );
      setReplyBody("");
    },
    [dispatch, id, replyBody]
  );

  const handleSendClientEmail = useCallback(
    async (payload) => {
      if (!id) return;
      try {
        await dispatch(sendServiceRequestEmail({ id, ...payload })).unwrap();
      } catch {
        /* toast from thunk */
      }
    },
    [dispatch, id]
  );

  const emailSending =
    (clientEmailOutbound && String(clientEmailOutbound.parentId) === String(id)) ||
    (Boolean(clientEmailSendHttpPending) && detail?.id != null && String(detail.id) === String(id));

  const handleRerunClientEmailAnalysis = useCallback(
    async (emailMessageId) => {
      if (!id) return;
      await dispatch(rerunServiceRequestClientEmailAnalysis({ id, emailMessageId })).unwrap();
    },
    [dispatch, id]
  );

  const confirmDeleteRequest = useCallback(() => {
    if (!id) return;
    dispatch(removeServiceRequest(id))
      .unwrap()
      .then(() => navigate("/admin/dashboard/requests"))
      .catch(() => {});
  }, [dispatch, id, navigate]);

  if (!apiOn) {
    return (
      <div className="admin-dash__page admin-dash__page--detail">
        <Link className="admin-dash__back" to="/admin/dashboard/requests">
          ← Service requests
        </Link>
        <h1 className="admin-dash__h1" style={{ marginTop: 16 }}>
          Admin API is off
        </h1>
        <p className="admin-dash__panel-sub" style={{ maxWidth: 520 }}>
          Enable the admin API for this build and sign in to load and edit service requests from your backend.
        </p>
      </div>
    );
  }

  if (id && (detailStatus === "idle" || detailStatus === "loading")) {
    return (
      <div className="admin-dash__page admin-dash__page--detail">
        <Link className="admin-dash__back" to="/admin/dashboard/requests">
          ← Service requests
        </Link>
        <div className="admin-dash__load-banner" role="status" style={{ marginTop: 16 }}>
          <span className="admin-dash__load-banner-dot" aria-hidden />
          Loading request…
        </div>
        <div className="admin-dash__overview-skeleton" style={{ marginTop: 24 }}>
          <div className="admin-dash__sk admin-dash__overview-skeleton-banner admin-dash__sk--pulse" />
          <div className="admin-dash__sk admin-dash__overview-skeleton-panel admin-dash__sk--pulse" />
          <div className="admin-dash__sk admin-dash__overview-skeleton-panel admin-dash__sk--pulse" />
        </div>
      </div>
    );
  }

  if (detailStatus === "failed") {
    return (
      <div className="admin-dash__page">
        <p className="admin-dash__empty-state-title">Could not load this request</p>
        <p className="admin-dash__td-muted" style={{ marginTop: 8 }}>
          {detailError || "Unknown error"}
        </p>
        <Link className="admin-dash__link" to="/admin/dashboard/requests" style={{ marginTop: 16, display: "inline-block" }}>
          ← Back to list
        </Link>
      </div>
    );
  }

  if (!row || !form) {
    return (
      <div className="admin-dash__page">
        <p className="admin-dash__empty">Request not found.</p>
        <Link className="admin-dash__link" to="/admin/dashboard/requests">
          ← Back to list
        </Link>
      </div>
    );
  }

  const replies = row.replies || [];
  const emailMessages = row.emailMessages ?? row.emailThread ?? [];
  const isCompany = form.requesterType === "company";
  const savedStatus = canonicalAdminStatus(row.status);
  const statusDirty = form.status !== savedStatus;
  const defaultEmailSubject = `Re: ${(form.serviceTitle || "").trim() || "Service request"} — ${(form.name || "").trim() || "VizHacks"}`;
  const analysisMeta = analysisChromeMeta(row.aiAnalysis?.status || "none");

  return (
    <div className="admin-dash__page admin-dash__page--detail">
      <header className="admin-dash__page-head">
        <Link className="admin-dash__back" to="/admin/dashboard/requests">
          ← Service requests
        </Link>
        <p className="admin-dash__page-eyebrow">Service requests</p>
        <h1 className="admin-dash__h1">{empty(form.name)}</h1>
        <p className="admin-dash__detail-meta">
          <time dateTime={row.createdAt}>{new Date(row.createdAt).toLocaleString()}</time>
          {row._demo ? <span className="admin-dash__tag-muted">sample</span> : null}
        </p>
        <div className="admin-dash__meta-row admin-dash__meta-row--badges">
          <span className="admin-dash__badge-type">
            {isCompany ? "Company" : "Individual"}
          </span>
          <span className={adminStatusBadgeClass(form.status)}>
            {adminStatusLabel(form.status)}
          </span>
          {form.serviceTitle ? (
            <span className="admin-dash__badge-soft">{form.serviceTitle}</span>
          ) : null}
        </div>
      </header>

      <section className="admin-dash__panel admin-dash__panel--workflow">
        <h2 className="admin-dash__h2">Client pipeline</h2>
        <p className="admin-dash__panel-sub admin-dash__panel-sub--inline">
          Track where this request sits relative to your offer: new, waiting on the client, confirmed,
          or rejected. Choose the stage, then Apply. Submitted fields stay read-only unless you choose
          Edit.
        </p>
        <div className="admin-dash__workflow-field admin-dash__workflow-field--rich">
          <span className="admin-dash__label admin-dash__label--workflow" id="req-pipeline-label">
            Stage
          </span>
          <div className="admin-dash__workflow-controls admin-dash__workflow-controls--stack">
            <AdminStatusWorkflow
              idPrefix="req-pipeline"
              ariaLabelledBy="req-pipeline-label"
              value={form.status}
              onChange={setPipelineStatus}
            />
            <div className="admin-dash__workflow-apply-row">
              <button
                type="button"
                className="admin-dash__btn-primary admin-dash__btn-primary--compact"
                disabled={!statusDirty}
                onClick={handleApplyStatus}
              >
                Apply stage
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="admin-dash__panel">
        <div className="admin-dash__panel-head">
          <h2 className="admin-dash__h2">Submitted request</h2>
          {!editingFields ? (
            <button
              type="button"
              className="admin-dash__btn-text"
              onClick={() => setEditingFields(true)}
            >
              Edit fields
            </button>
          ) : null}
        </div>

        {!editingFields ? (
          <dl className="admin-dash__detail-grid">
            <div className="admin-dash__detail-item">
              <dt className="admin-dash__detail-label">Service</dt>
              <dd className="admin-dash__detail-value">{empty(form.serviceTitle)}</dd>
            </div>
            <div className="admin-dash__detail-item">
              <dt className="admin-dash__detail-label">Email</dt>
              <dd className="admin-dash__detail-value">
                {form.email.trim() ? (
                  <a className="admin-dash__link" href={`mailto:${form.email.trim()}`}>
                    {form.email.trim()}
                  </a>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            {isCompany ? (
              <div className="admin-dash__detail-item">
                <dt className="admin-dash__detail-label">Company</dt>
                <dd className="admin-dash__detail-value">{empty(form.companyName)}</dd>
              </div>
            ) : null}
            <div className="admin-dash__detail-item admin-dash__detail-item--full">
              <dt className="admin-dash__detail-label">Requirements</dt>
              <dd className="admin-dash__detail-value admin-dash__detail-value--multiline">
                {empty(form.requirements)}
              </dd>
            </div>
            <div className="admin-dash__detail-item admin-dash__detail-item--full">
              <dt className="admin-dash__detail-label">Description</dt>
              <dd className="admin-dash__detail-value admin-dash__detail-value--multiline">
                {empty(form.description)}
              </dd>
            </div>
          </dl>
        ) : (
          <form className="admin-dash__form admin-dash__form--edit" onSubmit={handleSaveFields}>
            <label className="admin-dash__label">
              Service title
              <input
                className="admin-dash__input"
                name="serviceTitle"
                value={form.serviceTitle}
                onChange={handleField}
              />
            </label>
            <label className="admin-dash__label">
              Requester type
              <select
                className="admin-dash__input"
                name="requesterType"
                value={form.requesterType}
                onChange={handleField}
              >
                <option value="company">Company</option>
                <option value="individual">Individual</option>
              </select>
            </label>
            <label className="admin-dash__label">
              Name
              <input
                className="admin-dash__input"
                name="name"
                value={form.name}
                onChange={handleField}
                required
              />
            </label>
            <label className="admin-dash__label">
              Company
              <input
                className="admin-dash__input"
                name="companyName"
                value={form.companyName}
                onChange={handleField}
              />
            </label>
            <label className="admin-dash__label">
              Email
              <input
                className="admin-dash__input"
                type="email"
                name="email"
                value={form.email}
                onChange={handleField}
                required
              />
            </label>
            <label className="admin-dash__label">
              Requirements
              <textarea
                className="admin-dash__textarea"
                name="requirements"
                value={form.requirements}
                onChange={handleField}
                rows={4}
              />
            </label>
            <label className="admin-dash__label">
              Description
              <textarea
                className="admin-dash__textarea"
                name="description"
                value={form.description}
                onChange={handleField}
                rows={6}
              />
            </label>
            <div className="admin-dash__form-actions">
              <button type="submit" className="admin-dash__btn-primary">
                Save changes
              </button>
              <button type="button" className="admin-dash__btn-secondary" onClick={handleCancelEdit}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>

      <AdminCollapsiblePanel
        title="Request analysis"
        subtitle="AI summary, pricing hints, model queue & run history"
        defaultOpen={false}
        panelClassName="admin-dash__panel--collapsible-analysis"
        badge={<span className={analysisMeta.className}>{analysisMeta.label}</span>}
      >
        <RequestAnalysisPanel requestId={id} analysis={row.aiAnalysis} embedInCollapsible />
      </AdminCollapsiblePanel>

      <AdminCollapsiblePanel
        title="Internal replies"
        subtitle="Team-only — not emailed to the requester unless your backend sends mail"
        defaultOpen={false}
        panelClassName="admin-dash__panel--collapsible-thread"
        badge={
          replies.length > 0 ? (
            <span className="admin-dash__collapsible-count-badge">{replies.length}</span>
          ) : null
        }
      >
        {replies.length === 0 ? (
          <p className="admin-dash__td-muted">No replies yet.</p>
        ) : (
          <ul className="admin-dash__reply-list">
            {replies.map((rep) => (
              <li key={rep.id} className="admin-dash__reply">
                <div className="admin-dash__reply-head">
                  <div className="admin-dash__reply-meta">
                    <strong>{rep.author}</strong>
                    <time dateTime={rep.createdAt}>
                      {new Date(rep.createdAt).toLocaleString()}
                    </time>
                  </div>
                </div>
                <p className="admin-dash__reply-body">{rep.body}</p>
              </li>
            ))}
          </ul>
        )}
        <form className="admin-dash__reply-form" onSubmit={handleReply}>
          <label className="admin-dash__label">
            Add reply
            <textarea
              className="admin-dash__textarea"
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              rows={3}
              placeholder="Internal note or draft response…"
            />
          </label>
          <button type="submit" className="admin-dash__btn-primary" disabled={!replyBody.trim()}>
            Post reply
          </button>
        </form>
      </AdminCollapsiblePanel>

      <AdminEmailThreadPanel
        emailMessages={emailMessages}
        clientEmail={form.email}
        defaultSubject={defaultEmailSubject}
        onSendEmail={handleSendClientEmail}
        onRerunClientAnalysis={handleRerunClientEmailAnalysis}
        sending={emailSending}
        emailAnalysisRequestId={id}
        requestAiAnalysis={row.aiAnalysis}
      />

      <section className="admin-dash__danger-panel" aria-labelledby="req-delete-heading">
        <h2 id="req-delete-heading" className="admin-dash__h2 admin-dash__h2--danger">
          Delete request
        </h2>
        <p className="admin-dash__thread-note admin-dash__thread-note--danger">
          Permanently remove this request from the server. This cannot be undone.
        </p>
        <button type="button" className="admin-dash__btn-danger" onClick={() => setDeleteRequestOpen(true)}>
          Delete request
        </button>
      </section>

      <AdminTypedDeleteModal
        open={deleteRequestOpen}
        onClose={() => setDeleteRequestOpen(false)}
        title="Delete this service request?"
        description="This removes the request from the API. Replies and notes are removed with it. This cannot be undone."
        phrase="Delete"
        confirmButtonLabel="Delete request"
        onConfirm={confirmDeleteRequest}
      />
    </div>
  );
}
